import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Button } from './Button';

export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;

const widths = {
  sm: 'max-w-[400px]',
  md: 'max-w-[520px]',
  lg: 'max-w-[680px]',
  xl: 'max-w-[880px]',
} as const;

export function DialogOverlay({ className, ...props }: ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      className={cn(
        'fixed inset-0 z-50 animate-fade-in bg-backdrop backdrop-blur-[3px] data-[state=closed]:animate-fade-out',
        className,
      )}
      {...props}
    />
  );
}

export interface DialogContentProps extends ComponentProps<typeof DialogPrimitive.Content> {
  size?: keyof typeof widths;
  /** Hide the corner close button (e.g. while a submission is in flight). */
  hideClose?: boolean;
}

/** Centered modal: glass over the blurred scrim, a hairline and the floating shadow. */
export function DialogContent({ className, children, size = 'md', hideClose, ...props }: DialogContentProps) {
  return (
    <DialogPrimitive.Portal>
      <DialogOverlay />
      <DialogPrimitive.Content
        className={cn(
          'fixed top-1/2 left-1/2 z-50 flex max-h-[calc(100dvh-4rem)] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 flex-col',
          'rounded-xl border glass-modal text-fg shadow-float outline-none',
          'animate-dialog-in data-[state=closed]:animate-dialog-out',
          widths[size],
          className,
        )}
        {...props}
      >
        {children}
        {!hideClose && (
          <DialogPrimitive.Close asChild>
            <Button variant="ghost" size="icon-sm" className="absolute top-2 right-2" aria-label="Close">
              <X className="!size-3.5" />
            </Button>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

export function DialogHeader({
  title,
  description,
  className,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div className={cn('flex flex-col gap-0.5 border-b border-border px-4 py-2.5 pr-12', className)}>
      <DialogPrimitive.Title className="text-base font-semibold text-fg">{title}</DialogPrimitive.Title>
      {description ? (
        <DialogPrimitive.Description className="text-xs text-fg-subtle">
          {description}
        </DialogPrimitive.Description>
      ) : (
        <DialogPrimitive.Description className="sr-only">{title}</DialogPrimitive.Description>
      )}
      {children}
    </div>
  );
}

export function DialogBody({ className, ...props }: ComponentProps<'div'>) {
  return <div className={cn('min-h-0 flex-1 overflow-y-auto px-4 py-4', className)} {...props} />;
}

export function DialogFooter({
  className,
  children,
  aside,
}: {
  className?: string;
  children: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <div
      className={cn('flex items-center justify-between gap-3 border-t border-border px-4 py-3', className)}
    >
      <div className="min-w-0 text-xs text-fg-subtle">{aside}</div>
      <div className="flex shrink-0 items-center gap-2">{children}</div>
    </div>
  );
}

/** Right-hand drawer (Radix Dialog) for create/edit flows and quick looks: full height, flush to the edge. */
export function SheetContent({
  className,
  children,
  width = 'md',
  ...props
}: ComponentProps<typeof DialogPrimitive.Content> & { width?: 'md' | 'lg' }) {
  return (
    <DialogPrimitive.Portal>
      <DialogOverlay />
      <DialogPrimitive.Content
        className={cn(
          'fixed inset-y-0 right-0 z-50 flex w-[calc(100vw-1rem)] flex-col border-l glass-modal text-fg shadow-float outline-none',
          'animate-sheet-in data-[state=closed]:animate-sheet-out',
          width === 'md' ? 'max-w-[520px]' : 'max-w-[720px]',
          className,
        )}
        {...props}
      >
        {children}
        <DialogPrimitive.Close asChild>
          <Button variant="ghost" size="icon-sm" className="absolute top-2 right-2" aria-label="Close">
            <X className="!size-3.5" />
          </Button>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;

export function SheetHeader({ title, description }: { title: ReactNode; description?: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-border px-4 py-2.5 pr-12">
      <DialogPrimitive.Title className="text-base font-semibold text-fg">{title}</DialogPrimitive.Title>
      {description ? (
        <DialogPrimitive.Description className="text-xs text-fg-subtle">
          {description}
        </DialogPrimitive.Description>
      ) : (
        <DialogPrimitive.Description className="sr-only">{title}</DialogPrimitive.Description>
      )}
    </div>
  );
}

export function SheetBody({ className, ...props }: ComponentProps<'div'>) {
  return (
    <div
      className={cn('flex min-h-0 flex-1 flex-col gap-6 overflow-y-auto px-4 py-4', className)}
      {...props}
    />
  );
}

export function SheetFooter({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 border-t border-border px-4 py-3">
      <div className="min-w-0 text-xs text-fg-subtle">{aside}</div>
      <div className="flex shrink-0 items-center gap-2">{children}</div>
    </div>
  );
}

/** A titled group of fields inside a form. */
export function FormSection({
  title,
  description,
  children,
  className,
}: {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-col gap-0.5">
        <h3 className="micro-label">{title}</h3>
        {description && <p className="text-xs text-fg-subtle">{description}</p>}
      </div>
      {children}
    </section>
  );
}
